import {QueryClient, queryOptions, useQueries, useQueryClient} from "@tanstack/react-query";
import {
    BundleStub,
    ensureFullAllTransfers,
    ensureFullScheduledTransfersForDispatch,
    KingBasura,
    TruckStub,
    useActiveBundles,
    useFullAllTransfers,
    useFullScheduledTransfersForDispatch,
    watchBundleByIdTqo
} from "../rpc/webrpc-client.gen";
import {PropsWithChildren, useMemo} from "react";
import {
    ensureLastSceneForTruck,
    lastSceneForTruckTqo,
    TruckActor,
    TruckActors,
    TruckStubs,
    TruckTimelineRelay
} from "../truck/timeline.tsx";
import {BundleActor, BundleTimelineRelay, lastSceneForBundleTqo} from "../bundle/timeline.tsx";
import {BundleStatusRelay, bundleStatusTqo, unknownBundleStatus} from "../bundle/status.tsx";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {cleanse, indexesOf, NADA} from "../util/arutil.tsx";
import {mergeUseQueriesWithInfill} from "../tq/mergeUseQueriesWithInfill.tsx";
import {useMothership} from "../tq/tqx.tsx";

export const dispatchTruckActorStubsVerb = "DispatchTruckActorStubs";

export const dispatchTruckActorStubsTqk = [dispatchTruckActorStubsVerb] as const;

function ensureDispatchTruckStubs(args: {
    tqc: QueryClient,
    mama: KingBasura
}): Promise<TruckStubs> {
    return Promise.all([
        ensureFullAllTransfers(args),
        ensureFullScheduledTransfersForDispatch(args),
    ]).then(([transferRpcs, scheduleRpcs]) => {
        const trucks = new Map<string, TruckStub>();

        transferRpcs.forEach(rpc => {
            trucks.set(rpc.transfer.truck.id, rpc.transfer.truck);
        });
        scheduleRpcs.forEach(rpc => {
            if (rpc.scheduledTransfer?.truck) {
                trucks.set(rpc.scheduledTransfer.truck.id, rpc.scheduledTransfer.truck);
            }
        });

        return {truckStubs: Array.from(trucks.values())};
    });
}

export function dispatchTruckStubsTqo(args: {
    tqc: QueryClient,
    mama: KingBasura,
}) {
    return queryOptions({
        queryKey: dispatchTruckActorStubsTqk,
        queryFn: () => ensureDispatchTruckStubs(args),
        select: (rpc) => rpc.truckStubs,
    });
}

export function useDispatchTruckActorStubs(): InfallibleUseResult<TruckStub[]> {
    const transferTqrv = useFullAllTransfers();
    const scheduleTqrv = useFullScheduledTransfersForDispatch();

    if (transferTqrv.isPending || scheduleTqrv.isPending) {
        return pendingUseResult;
    }

    const stubsById = new Map<string, TruckStub>();
    transferTqrv.data.forEach(transfer => {
        if (transfer) {
            stubsById.set(transfer.truck.id, transfer.truck);
        }
    });
    scheduleTqrv.data.forEach(sxfer => {
        if (sxfer?.truck) {
            stubsById.set(sxfer.truck.id, sxfer.truck);
        }
    });

    return successUseResult(Array.from(stubsById.values()));
}

export const dispatchTruckActorsVerb = "DispatchTruckActors";

export const dispatchTruckActorsTqk = [dispatchTruckActorsVerb] as const;

function ensureDispatchTruckActors(args: {
    tqc: QueryClient,
    mama: KingBasura
}): Promise<TruckActors> {
    return ensureDispatchTruckStubs(args)
        .then(({truckStubs: dirtyStubs}) => {
            const stubs = cleanse(dirtyStubs);
            return Promise.all(stubs.map(stub =>
                ensureLastSceneForTruck({tqc: args.tqc, mama: args.mama, args: {truckId: stub.id}})))
                .then(scenes => {
                    // const scenesBySubjectId = new Map<string, TruckScene>(cleanse(scenes).map(scene =>
                    //     ([scene.subject.id, scene])));
                    const rv: TruckActors = {
                        truckActors: indexesOf(stubs).map(ix => {
                            const atruck: TruckActor = {
                                truck: stubs[ix]!,
                                lastScene: scenes[ix] ?? null,
                            };
                            return atruck;
                        })
                    };
                    return rv;
                });
        });
}

export function dispatchTruckActorsTqo(args: {
    tqc: QueryClient,
    mama: KingBasura,
}) {
    return queryOptions({
        queryKey: dispatchTruckActorsTqk,
        queryFn: () => ensureDispatchTruckActors(args),
        select: (rpc) => rpc.truckActors,
    });
}

export function useDispatchTruckActors(): InfallibleUseResult<TruckActor[]> {
    const mama = useMothership();
    const stubsTqrv = useDispatchTruckActorStubs();
    const scenesTqrv = useQueries(stubsTqrv.data ?
        {
            queries: stubsTqrv.data.map(stub => lastSceneForTruckTqo(mama, {truckId: stub.id})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    const rv = useMemo<InfallibleUseResult<TruckActor[]>>(() => {
        if (stubsTqrv.isPending || scenesTqrv.isPending) {
            return pendingUseResult;
        }

        const stubs = stubsTqrv.data;
        const rv = indexesOf(stubs).map(ix => {
            const truck = stubs[ix]!;
            const lastScene = scenesTqrv.data[ix] ?? null;
            const rv: TruckActor = {truck, lastScene};
            return rv;
        });
        return successUseResult(rv);
    }, [scenesTqrv.data, scenesTqrv.isPending, stubsTqrv.data, stubsTqrv.isPending]);

    return rv;
}

//
// const truckActorsForDispatchTqk: QueryKey = [truckActorsForDispatchVerb];
//
// function truckActorsForDispatchTqo(tqc: QueryClient, mama: KingBasuraService) {
//     const transferFlesher =
//     return queryOptions({
//         queryKey: truckActorsForDispatchTqk,
//         queryFn: () => Promise.all([
//             tqc.ensureQueryData(watchOpenBundleTransfersTqo(mama)),
//             tqc.ensureQueryData(watchScheduledTransfersForDispatchTqo(mama)),
//         ]).then(([openTransferStubsRpc, scheduledTransferStubsRpc]) => Promise.all([
//
//             Promise.all(openTransferStubsRpc.transfers.map(stub => tqc.ensureQueryData)
//
//     }
//     })
// }
//
// interface TruckUpdatables {
//     lastTripleg?: Tripleg;
//     flavoredStamps: TruckStamp[];
//     crew: AgentStub[];
// }
//
// function fetchDispatchTruck({tqc, mama, truckId}: {
//     tqc: QueryClient,
//     mama: KingBasuraService,
//     truckId: string
// }): Promise<DispatchTruck> {
//
// }
//
// function DispatchTruckWatcher()
//
// export function useDispatchTruckOrdering(cmp: Comparator<DispatchTruck>) {
//     const store = useDispatchTruckStore();
//
//     useEffect(() => {
//         if (cmp !== store._istate.cmp) {
//             store.api.orderBy(cmp);
//         }
//     }, [cmp, store._istate.cmp, store.api]);
//
//     return store.state.ordered;
// }

export const dispatchBundleActorStubsVerb = "DispatchBundleActorStubs";

export const dispatchBundleActorStubsTqk = [dispatchBundleActorStubsVerb] as const;

// function ensureDispatchBundleActorStubs(args: {
//     tqc: QueryClient,
//     mama: KingBasuraService
// }): Promise<BundleStubs> {
//     return ensureActiveBundles(args)
//         .then(({bundles: stubs}) => ({bundleStubs: stubs}));
// }
//
// function dispatchBundleActorStubsTqo(args: { tqc: QueryClient, mama: KingBasuraService }) {
//     return queryOptions({
//         queryKey: dispatchBundleActorStubsTqk,
//         queryFn: () => ensureDispatchBundleActorStubs(args),
//         select: rpc => rpc.bundleStubs,
//     });
// }

function useDispatchBundleActorStubs(): InfallibleUseResult<BundleStub[]> {
    const activeTqrv = useActiveBundles();
    if (activeTqrv.isPending) {
        return pendingUseResult;
    } else {
        return successUseResult(activeTqrv.data);
    }
}

export function useDispatchBundleActors() {
    const tqc = useQueryClient();
    const mama = useMothership();
    const stubsTqrv = useDispatchBundleActorStubs();
    // const stubs = useMemo(() => stubsTqrv.data ?? [], [stubsTqrv.data]);

    const bundlesTqrv = useQueries(stubsTqrv.data
        ? {
            queries: stubsTqrv.data.map(stub => watchBundleByIdTqo(mama, {bundleId: stub.id})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});
    const scenesTqrv = useQueries(stubsTqrv.data
        ? {
            queries: stubsTqrv.data.map(stub => lastSceneForBundleTqo(mama, {bundleId: stub.id})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});
    const statusenTqrv = useQueries(stubsTqrv.data
        ? {
            queries: stubsTqrv.data.map(stub => bundleStatusTqo({tqc, mama, args: {bundleId: stub.id}})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    const rv = useMemo<InfallibleUseResult<BundleActor[]>>(() => {
        if (stubsTqrv.isPending || bundlesTqrv.isPending || scenesTqrv.isPending || statusenTqrv.isPending) {
            return pendingUseResult;
        }

        const stubs = stubsTqrv.data;

        const rv: BundleActor[] = cleanse(indexesOf(stubs).map(ix => {
            const bundle = bundlesTqrv.data[ix];
            if (!bundle) {
                return undefined;
            }
            const lastScene = scenesTqrv.data[ix] ?? null;
            const status = statusenTqrv.data[ix] ?? unknownBundleStatus;
            return {bundle, lastScene, status} as const;
        }));

        // const scenes = cleanse(scenesTqrv.data);
        // const scenesBySubjectId = new Map<string, BundleScene>(scenes.map(scene => {
        //     return [scene.subject.id, scene];
        // }));
        // const statusByBundleId = new Map<string, BundleStatus>()
        // const rv = stubs.map(stub => {
        //     const lastScene = scenesBySubjectId.get(stub.id) ?? null;
        //     return {bundle: stub, lastScene};
        // });
        return successUseResult(rv);
    }, [bundlesTqrv.data, bundlesTqrv.isPending,
        scenesTqrv.data, scenesTqrv.isPending,
        statusenTqrv.data, statusenTqrv.isPending, stubsTqrv.data, stubsTqrv.isPending]);

    return rv;
}

// export function DispatchBundleTimelineRelay() {
//     const bundlesTqrv = useBundleActors();
//     const bundles = bundlesTqrv.data;
//     return <>
//         {(bundles ?? []).map(bundle =>
//             <BundleTimelineRelay bundleId={bundle.id} key={bundle.id}/>)}
//     </>;
// }

// export type DispatchBundleOrderState = TouchableOrderings<DispatchBundle, typeof dispatchBundleSorters>;

// const BundleDynaMapContext = createContext<DynaMapApi<DispatchBundle, string>>(deadbeefDynaMapApi);
//
// export function useDispatchBundleOrdering(cmp: Comparator<DispatchBundle>) {
//     const store = useDispatchBundleStore();
//
//     useEffect(() => {
//         if (cmp !== store._istate.cmp) {
//             store.api.orderBy(cmp);
//         }
//     }, [cmp, store.api, store._istate.cmp]);
//
//     return store.state.ordered;
// }
//
// export type BootTemp = "cold" | "warm" | "hot" | "dead";
//
// const BootTempContext = createContext<BootTemp>("dead");
//
// export function useDispatchMeshBootTemp() {
//     return useContext(BootTempContext);
// }
//
// // don't die without a provider here, or you cause unwanted errors
// // for people without dispatch privileges.
// // eslint-disable-next-line @typescript-eslint/no-empty-function
// const ResetContext = createContext(() => {
// });
//
// export function useResetDispatchMesh() {
//     return useContext(ResetContext);
// }
//
// function DispatchMeshWatcher({children}: PropsWithChildren) {
//     const mama = useMothership();
//     const {data: openTransferStubsData} = useQuery(watchOpenBundleTransfersTqo(mama));
//     const openTransferStubs = openTransferStubsData?.transfers ?? [];
//     const transferCombiner =
//         useMappingMergeUseQueryResultCombiner((rpc: WatchBundleTransferByIdReturn) => rpc.transfer);
//     const {data: openTransfers} = useQueries({
//         queries: openTransferStubs.map(stub =>
//             watchBundleTransferByIdTqo(mama, {transferId: stub.id})),
//         combine: transferCombiner,
//     });
//     const {data: scheduledTransferStubsData} = useQuery(watchScheduledTransfersForDispatchTqo(mama));
//     const scheduledTransferStubs = scheduledTransferStubsData?.scheduledTransfers ?? [];
//     const scheduledTransferCombiner =
//         useMappingMergeUseQueryResultCombiner((rpc: WatchScheduledTransferForBundleReturn) => rpc.scheduledTransfer);
//     const {data: scheduledTransfers}
//         = useQueries({
//         queries: scheduledTransferStubs.map(stub => watchScheduledBundleTransferByIdTqo(mama, {scheduledTransferId: stub.id})),
//         combine: scheduledTransferCombiner
//     });
//
//     const relevantTruckIds = useMemo(() => {
//         const rvset = new Set<string>();
//         if (openTransfers) {
//             for (const xfer of openTransfers) {
//                 if (xfer.truck) {
//                     rvset.add(xfer.truck.id);
//                 }
//             }
//         }
//         if (scheduledTransfers) {
//             for (const sxfer of scheduledTransfers) {
//                 if (sxfer.truck) {
//                     rvset.add(sxfer.truck.id);
//                 }
//             }
//         }
//
//         return Array.from(rvset.keys());
//     }, [openTransfers, scheduledTransfers]);
//
//     return <>
//         {relevantTruckIds.map(truckId =>
//             <TruckTimelineRelay key={truckId} truckId={truckId}/>)}
//         {children}
//     </>
// }
//
// function DispatchMeshWatcher({children}: PropsWithChildren) {
//     const tqc = useQueryClient();
//     const mama = useMothership();
//     const bomb = useBomb();
//     const cleanupApi = useDeferredCleanup();
//
//     const [bootTemp, rawSetBootTemp] = useState<BootTemp>("cold");
//
//     const bundleDynaMap = useDispatchBundleStore();
//     const bundleDmapi = bundleDynaMap.api;
//     const truckDynaMap = useDispatchTruckStore();
//     const truckDmapi = truckDynaMap.api;
//     // const watchedTriplegIdSetRef = useRef<Set<string>>(new Set<string>());
//
//     const setBootTemp = useCallback((next: BootTemp) => {
//         console.log(`shift dispatch boot temp to ${next}`);
//
//         if (next === "cold" || next === "dead") {
//             cleanupApi.perform();
//             truckDmapi.clear();
//             bundleDmapi.clear();
//         }
//
//         rawSetBootTemp(next);
//     }, [bundleDmapi, cleanupApi, truckDmapi]);
//
//     const reset = useCallback(() => {
//         setBootTemp("cold");
//     }, [setBootTemp]);
//
//     const initBundle = useCallback((bundleId: string) => {
//         const rv: Promise<unknown> = Promise.all([
//             tqc.ensureQueryData(watchBundleByIdTqo(mama, {bundleId})),
//             tqc.ensureQueryData(lastFlavoredStampForBundleTqo(mama, {bundleId})),
//             tqc.ensureQueryData(watchLastTransferForBundleTqo(mama, {bundleId})),
//             tqc.ensureQueryData(watchScheduledTransferForBundleTqo(mama, {bundleId})),
//         ]).then(([bundleData, stampData, transferData, scheduledTransferData]) => {
//             const bundle = bundleData.bundle;
//             const lastStamp = stampData.stamp;
//             const transfer = transferData.transfer;
//             const scheduledTransfer = scheduledTransferData.scheduledTransfer;
//             let tripleg$: Promise<Tripleg | undefined> = Promise.resolve(undefined);
//             if (!!transfer && !!transfer.onloadStarted && !transfer.onloadEnded && !!transfer.truck) {
//                 tripleg$ = tqc.ensureQueryData(
//                     watchLastTriplegForTruckTqo(mama, {truckId: transfer.truck.id}))
//                     .then(tqrv => tqrv.tripleg);
//             } else if (!!scheduledTransfer && scheduledTransfer.truck?.id) {
//                 tripleg$ = tqc.ensureQueryData(
//                     watchLastTriplegForTruckTqo(mama, {truckId: scheduledTransfer.truck.id}))
//                     .then(tqrv => {
//                         if (tqrv.tripleg?.destination.id === scheduledTransfer.venue.id) {
//                             return tqrv.tripleg;
//                         } else {
//                             return undefined;
//                         }
//                     });
//             }
//             return tripleg$.then((tripleg) => {
//                 return {
//                     bundle, lastStamp, transfer, scheduledTransfer, tripleg
//                 };
//             });
//         }).then(({bundle, lastStamp, transfer, scheduledTransfer, tripleg}) => {
//             if (bundle) {
//                 const xdxb = bundleDmapi.getByKey(bundle.id);
//                 if (!xdxb) {
//                     let lastUpdated = new TstzSpan(bundle.validSpan).lowerDate()!;
//
//                     if (lastStamp?.posted && isAfter(lastStamp.posted, lastUpdated)) {
//                         lastUpdated = new Date(lastStamp.posted);
//                     }
//
//                     if (transfer) {
//                         if (!!transfer.onloadStarted && !transfer.onloadEnded) {
//                             lastUpdated = new Date(transfer.onloadStarted);
//                         } else if (!!transfer.onloadEnded && !transfer.offloadStarted) {
//                             if (transfer.truck) {
//                                 if (tripleg) {
//                                     lastUpdated = new Date(tripleg.lastModified);
//                                 }
//                             }
//                         } else if (!transfer.onloadStarted && !transfer.onloadEnded &&
//                             !transfer.offloadStarted && !transfer.offloadEnded) {
//                             // this is 持ち込み
//                             lastUpdated = new Date(transfer.lastModified);
//                         } else if (!!transfer.offloadStarted && !transfer.offloadEnded) {
//                             lastUpdated = new Date(transfer.offloadStarted);
//                         } else if (transfer.offloadEnded) {
//                             lastUpdated = new Date(transfer.offloadEnded);
//                         }
//                     }
//
//                     if (scheduledTransfer && !!scheduledTransfer.arrivalWindow) {
//                         const maybe = new TstzSpan(scheduledTransfer.arrivalWindow).lowerDate();
//                         if (maybe) {
//                             lastUpdated = maybe;
//                         }
//                     }
//
//                     if (tripleg) {
//                         lastUpdated = new Date(tripleg.lastModified);
//                     }
//
//                     bundleDmapi.replace({bundle, lastTouched: lastUpdated});
//                 }
//             }
//         });
//         return rv;
//     }, [bundleDmapi, mama, tqc]);
//
//     const ensureBundle = useCallback((bundleId: string) => {
//         const rv = bundleDmapi.getByKey(bundleId);
//         if (rv) {
//             // console.log(`ensureBundle cache hit ${JSON.stringify(rv)}`)
//             return Promise.resolve(rv);
//         } else {
//             return initBundle(bundleId);
//         }
//     }, [bundleDmapi, initBundle]);
//
//     const touchOrInitBundle = useCallback(({bundleId, asof}: { bundleId: string, asof?: string | Date }) => {
//         const xrec = bundleDmapi.getByKey(bundleId);
//         if (xrec) {
//             const next = {...xrec, lastTouched: asof ?? new Date()};
//             bundleDmapi.replace(next);
//         } else {
//             initBundle(bundleId).catch(bomb);
//         }
//     }, [bomb, bundleDmapi, initBundle]);
//
//     const initTruck = useCallback((stub: TruckStub) => {
//         // console.log(`start initializing ${JSON.stringify(stub)}`);
//         const rv: Promise<{ dxt: DispatchTruck, isNew: boolean }> = Promise.all([
//             tqc.ensureQueryData(lastFlavoredStampForTruckTqo(mama, {truckId: stub.id})),
//             tqc.ensureQueryData(watchLastTriplegForTruckTqo(mama, {truckId: stub.id})),
//         ])
//             .then(([{stamp: lastStamp}, {tripleg}]) => {
//                 const xdxt = truckDmapi.getByKey(stub.id);
//                 if (!xdxt) {
//                     let lastUpdated = new Date(minTime);
//                     if (lastStamp?.posted) {
//                         lastUpdated = new Date(lastStamp.posted);
//                     }
//                     if (tripleg) {
//                         lastUpdated = new Date(tripleg.lastModified);
//                     }
//                     // console.log(`touching dispatch truck ${JSON.stringify(stub)}`);
//                     const newdxt = {truckStub: stub, lastTouched: lastUpdated};
//                     truckDmapi.replace(newdxt);
//                     return {dxt: newdxt, isNew: true};
//                 } else {
//                     return {dxt: xdxt, isNew: false};
//                 }
//             });
//         return rv;
//     }, [mama, tqc, truckDmapi]);
//
//     const ensureTruck = useCallback((stub: TruckStub) => {
//         const rv = truckDmapi.getByKey(stub.id);
//         if (rv) {
//             // console.log(`ensureTruck cache hit ${JSON.stringify(rv)}`)
//             return Promise.resolve({dxt: rv, isNew: false});
//         } else {
//             return initTruck(stub);
//         }
//     }, [initTruck, truckDmapi]);
//
// // it should be safe to assume that there can only be one transfer open
// // on a given bundle and truck at any given time. not true for venues.
// // you should only call this when the transfer represents your
// // first introduction to the bundle.
//
//     const initFromScheduledTransfer = useCallback((sxfer: ScheduledBundleTransfer) => {
//         return initBundle(sxfer.bundleId);
//     }, [initBundle]);
//
// const initFromOpenTransfer = useCallback((xfer: BundleTransfer) => {
//     const todo: Promise<unknown>[] = [initBundle(xfer.bundleId)];
//     if (xfer.truck) {
//         todo.push(ensureTruck(xfer.truck));
//     }
//     return Promise.all(todo);
// }, [ensureTruck, initBundle]);
//
// const handleBundleByIdEvent = useCallback((evt: TqCacheUpdate<WatchBundleByIdReturn>) => {
//     const bundleId = extractQueryKeyArg(evt.query.queryKey, "bundleId");
//     if (bundleId) {
//         const nextTqrv = evt.data;
//         if (nextTqrv) {
//             if (nextTqrv.bundle) {
//                 const nextBundle = nextTqrv.bundle;
//                 const nextValidSpan = nextBundle.validSpan;
//                 bundleDmapi.patch(bundleId, (prev) => {
//                     if (prev?.bundle.validSpan === nextValidSpan) {
//                         return prev;
//                     }
//
//                     const prevTouched = prev?.lastTouched;
//                     const nextValidStarting = new TstzSpan(nextValidSpan).lower;
//
//                     const nextTouched = nextValidStarting && (!prevTouched || isAfter(nextValidStarting, prevTouched))
//                         ? nextValidStarting
//                         : (prevTouched ?? new Date());
//
//                     const next: DispatchBundle = {bundle: nextBundle, lastTouched: nextTouched};
//                     return next;
//                 });
//             } else {
//                 bundleDmapi.evict(bundleId);
//             }
//         }
//     }
// }, [bundleDmapi]);
//
// const touchAllBundlesOnTruck = useCallback(({truckId, asof: inAsof}: {
//     truckId: string,
//     asof?: string | Date
// }) => {
//     const asof = inAsof ?? new Date();
//     tqc.ensureQueryData(watchOpenBundleTransfersForTruckTqo(mama, {truckId: truckId}))
//         .then(tqrv => Promise.all(
//             tqrv.transfers.map(xfer =>k
//                 tqc.ensureQueryData(watchBundleTransferByIdTqo(mama, {transferId: xfer.id}))
//                     .then((xfertqrv) =>
//                         tqc.ensureQueryData(watchBundleByIdTqo(mama, {bundleId: xfertqrv.transfer.bundleId})))
//                     .then(btqrv => {
//                         if (btqrv.bundle) {
//                             // TODO this does not seem to be getting through to bundles
//                             bundleDmapi.replace({bundle: btqrv.bundle, lastTouched: asof});
//                         }
//                     }))
//         )).catch(bomb);
// }, [bomb, bundleDmapi, mama, tqc]);
//
// const handleLastTriplegForTruckEvent = useCallback((evt: TqCacheUpdate<WatchLastTriplegForTruckReturn>) => {
//     const tripleg = evt.data?.tripleg;
//     if (tripleg) {
//         const nextDxt: DispatchTruck = {
//             truckStub: tripleg.truck,
//             lastTouched: tripleg.lastModified,
//         };
//         truckDmapi.replace(nextDxt);
//
//         touchAllBundlesOnTruck({truckId: nextDxt.truckStub.id, asof: nextDxt.lastTouched});
//
//         tqc.ensureQueryData(watchScheduledTransfersAwaitingTruckTqo(mama, {
//             truckId: nextDxt.truckStub.id, venueId: tripleg.destination.id
//         })).then(({scheduledTransfers}) =>
//             Promise.all(scheduledTransfers.map(stub =>
//                 tqc.ensureQueryData(watchScheduledBundleTransferByIdTqo(mama, {scheduledTransferId: stub.id}))
//                     .then(({scheduledTransfer}) => {
//                         if (scheduledTransfer?.bundleId) {
//                             touchOrInitBundle({
//                                 bundleId: scheduledTransfer.bundleId,
//                                 asof: scheduledTransfer.posted
//                             });
//                         }
//                     }))))
//             .catch(bomb);
//     }
// }, [bomb, mama, touchAllBundlesOnTruck, touchOrInitBundle, tqc, truckDmapi]);
//
// const consumeTriplegPacket = useCallback((packet: SetlistPacket<Tripleg>) => {
//     const triplegId = packet.next?.id ?? packet.prev?.id;
//     if (!triplegId) {
//         return
//     }
//
//     // TODO: this doesn't work because delta compression never adds queries that didn't
//     // previously exist. so the plan is to feed triplegbyid here, and add it to a list
//     // of watched tripleg ids that we ask useQueries for.
//     const byIdTqk = triplegByIdTqk({triplegId});
//     const cachedTqrv = tqc.getQueryData<WatchTriplegByIdReturn>(byIdTqk);
//     let cached = cachedTqrv?.tripleg ?? null;
//     if (!cached && !packet.prev) {
//         tqc.setQueryData(byIdTqk, {tripleg: packet.next});
//         cached = packet.next;
//         watchedTriplegIdSetRef.current.add(packet.next!.id);
//     }
//     if (packet.next?.admitted) {
//         watchedTriplegIdSetRef.current.delete(packet.next.id);
//     }
//     // we assume triplegs cannot change trucks in midstream
//     const truckStub = packet.next?.truck ?? packet.prev?.truck ?? cached?.truck;
//     if (truckStub) {
//         ensureTruck(truckStub)
//             .then(({dxt, isNew}) => {
//                 const now = new Date(packet.asof);
//                 if (!isNew) {
//                     const next: DispatchTruck = {...dxt, lastTouched: now};
//                     truckDmapi.replace(next);
//                 }
//                 const destination = packet.next?.destination ?? cached?.destination;
//                 if (destination) {
//                     return Promise.all([
//                         tqc.ensureQueryData(watchScheduledTransfersAwaitingTruckTqo(mama, {
//                             truckId: truckStub.id,
//                             venueId: destination.id,
//                         })).then(tqrv =>
//                             Promise.all(tqrv.scheduledTransfers.map(sxferStub =>
//                                 tqc.ensureQueryData(watchScheduledBundleTransferByIdTqo(mama, {scheduledTransferId: sxferStub.id}))
//                                     .then(fleshSxferTqrv => {
//                                         const bundleId = fleshSxferTqrv.scheduledTransfer?.bundleId;
//                                         if (bundleId) {
//                                             return tqc.ensureQueryData(watchBundleByIdTqo(mama, {bundleId}))
//                                                 .then(btqrv => {
//                                                     if (btqrv.bundle) {
//                                                         bundleDmapi.replace({
//                                                             bundle: btqrv.bundle,
//                                                             lastTouched: now
//                                                         });
//                                                     }
//                                                 });
//                                         } else {
//                                             return null;
//                                         }
//                                     })))),
//                         tqc.ensureQueryData(watchOpenBundleTransfersForTruckTqo(mama, {truckId: truckStub.id}))
//                             .then(tqrv => Promise.all(
//                                 tqrv.transfers.map(xfer =>
//                                     tqc.ensureQueryData(watchBundleTransferByIdTqo(mama, {transferId: xfer.id}))
//                                         .then((xfertqrv) =>
//                                             tqc.ensureQueryData(watchBundleByIdTqo(mama, {bundleId: xfertqrv.transfer.bundleId})))
//                                         .then(btqrv => {
//                                             if (btqrv.bundle) {
//                                                 // TODO this does not seem to be getting through to bundles
//                                                 bundleDmapi.replace({bundle: btqrv.bundle, lastTouched: now});
//                                             }
//                                         }))
//                             )),
//                     ]);
//                 }
//             }).catch(bomb);
//     }
// }, [bomb, bundleDmapi, ensureTruck, mama, tqc, truckDmapi]);

// const handleScheduledTransferForBundleEvent = useCallback((evt: TqCacheUpdate<WatchScheduledTransferForBundleReturn>) => {
//     const bundleId = extractQueryKeyArg(evt.query.queryKey, "bundleId");
//     if (bundleId) {
//         const sxfer = evt.data?.scheduledTransfer;
//         if (sxfer) {
//             touchOrInitBundle({bundleId, asof: sxfer.lastModified});
//         }
//         // TODO: i'm not sure we want to abandon these, because they are orphaned.
//         // else {
//         //     bundleDmapi.evict(bundleId);
//         // }
//     }
// }, [touchOrInitBundle]);
//
// const handleTransferEvent = useCallback((evt: TqCacheUpdate<WatchLastTransferForBundleReturn>) => {
//     const bundleId = extractQueryKeyArg(evt.query.queryKey, "bundleId");
//     if (bundleId) {
//         const transfer = evt.data?.transfer;
//         if (transfer) {
//             const truckStub = transfer.truck;
//             if (truckStub) {
//                 ensureTruck(truckStub).catch(bomb);
//             }
//
//             const bundleId = transfer.bundleId;
//             if (bundleId) {
//                 touchOrInitBundle({bundleId, asof: transfer.lastModified});
//             }
//         }
//     }
// }, [bomb, ensureTruck, touchOrInitBundle]);

//
//     // TODO there are many different kinds of setlist packets that actually start
//     // with truck_crew, and they look different.
//     // this should be dealt with more explicitly, but for now, gating everything
//     // should be safe, because neither the "crew" nor "truck" fields will be in
//     // TQK downstream packets. it might suffice to ignore anything upstream from here
//     // that has explicit tqk targeting in its path anyway.
//     private consumeTruckCrewPacket(packet: SetlistPacket<TruckCrewTenure>) {
//         const crewId = packet.next?.crew.id ?? packet.prev?.crew.id;
//         if (crewId) {
//             void GLOBAL_QUERY_CLIENT.invalidateQueries({queryKey: truckByCrewTqk({crewId})});
//         }
//         const truckId = packet.next?.truck.id ?? packet.prev?.truck.id;
//         if (truckId) {
//             void GLOBAL_QUERY_CLIENT.invalidateQueries({queryKey: crewByTruckTqk({truckId})})
//                 .then(() =>
//                     GLOBAL_QUERY_CLIENT.invalidateQueries({queryKey: activeCrewPresencesForTruckTqk({truckId})}));
//         }
//     }
// }
//

// const handleBundleStampEvent = useCallback((evt: TqCacheUpdate<WatchStampsForBundleReturn>) => {
//     const newestStamp = (evt.data?.stamps ?? []).reduce((sofar: BundleStamp | null, maybe) => {
//         if (!sofar || isAfter(maybe.posted, sofar.posted)) {
//             return maybe;
//         } else {
//             return sofar;
//         }
//     }, null);
//     if (newestStamp) {
//         const bundleId = newestStamp.subjectId;
//         touchOrInitBundle({bundleId, asof: newestStamp.posted});
//     }
// }, [touchOrInitBundle]);

// const consumeSetlistPacket = useCallback((packet: SetlistPacket<unknown>) => {
//     // console.log(`received for dispatch ${JSON.stringify(packet)}`);
//     const tbl = packet.tbl;
//     switch (tbl) {
//         case SetlistTable_BundleActive:
//             consumeBundlePacket(packet as SetlistPacket<Bundle>);
//             break;
//         // case Initiator_WasteActive:
//         //     this.consumeWastePacket(packet as SetlistPacket<Waste>);
//         //     break;
//         // case Initiator_WasteBundle:
//         //     this.consumeWasteBundlePacket(packet as SetlistPacket<WasteBundleSetlistEntry>);
//         //     break;
//         // // case Initiator_JobStamp:
//         //     this.consumeJobStampPacket(packet as SetlistPacket<JobStamp>);
//         //     break;
//         // case Initiator_JobStamp:
//         //     this.consumeWasteStampPacket(packet as SetlistPacket<WasteStamp>);
//         //     break;
//         // case Initiator_AgentPresence:
//         //     this.consumeAgentPresencePacket(packet as SetlistPacket<AgentPresence>);
//         //     break;
//         case SetlistTable_Tripleg:
//             consumeTriplegPacket(packet as SetlistPacket<Tripleg>);
//             break;
//         case SetlistTable_ScheduledBundleTransfer:
//             consumeScheduledTransferPacket(packet as SetlistPacket<ScheduledBundleTransfer>);
//             break;
//         case SetlistTable_BundleTransfer:
//             consumeTransferPacket(packet as SetlistPacket<BundleTransfer>);
//             break;
//         case SetlistTable_BundleStamp:
//             consumeBundleStampPacket(packet as SetlistPacket<BundleStamp>);
//             break;
//         default:
//         // console.log(`dispatch ignoring setlist table ${initiator}`);
//     }
// }, [consumeBundlePacket, consumeBundleStampPacket, consumeScheduledTransferPacket, consumeTransferPacket, consumeTriplegPacket]);

//     const tqspies = useMemo<TqCacheSpySet>(() => ({
//         BundleById: handleBundleByIdEvent,
//         LastTriplegForTruck: handleLastTriplegForTruckEvent,
//         ScheduledTransferForBundle: handleScheduledTransferForBundleEvent,
//         LastTransferForBundle: handleTransferEvent,
//         FlavoredStampsForBundle: handleBundleStampEvent,
//     }), [handleBundleByIdEvent, handleBundleStampEvent, handleLastTriplegForTruckEvent, handleScheduledTransferForBundleEvent, handleTransferEvent]);
//
//     // useWhatChanged([bomb, bootTemp, ensureBundle, ensureTruck, mama, tqc]);
//     useEffect(() => {
//         if (bootTemp === "cold") {
//             setBootTemp("warm");
//             cleanupApi.add(watchTqCacheByVerb({tqc, spies: tqspies}));
//             Promise.all([
//                 tqc.ensureQueryData(truckActorsForDispatchTqo(mama))
//                     .then(({trucks}) =>
//                         Promise.all(trucks.map(truck => ensureTruck(truck)))),
//                 tqc.ensureQueryData(watchBundleActorsTqo(mama))
//                     .then(({bundles}) =>
//                         Promise.all(bundles.map(bundle => ensureBundle(bundle.id)))),
//             ]).then(() => {
//                 setBootTemp("hot");
//             }).catch((xc: unknown) => {
//                 setBootTemp("dead");
//                 bomb(xc);
//             });
//         }
//     }, [bomb, bootTemp, cleanupApi, ensureBundle, ensureTruck, mama, setBootTemp, tqc, tqspies]);
//
//     useEffect(() => {
//         return () => {
//             setBootTemp("cold");
//         }
//     }, [setBootTemp]);
//
//     return <BootTempContext value={bootTemp}>
//         <ResetContext value={reset}>
//             {children}
//         </ResetContext>
//     </BootTempContext>
// }

// function DispatchMeshProvider({children}: PropsWithChildren) {
//
//
//     // const onTruckTouched = useCallback((nv: Touchable<DispatchTruck>) => {
//     //     tqc.setQueryData(truckLastUpdatedTqk({truckId: nv.value.truckStub.id}), nv);
//     // }, [tqc]);
//     //
//     // const onStableTruckOrdering = useCallback((dxts: RefStableArray<Touchable<DispatchTruck>>) => {
//     //     tqc.setQueryData(dispatchTrucksStableOrderTqk, dxts);
//     // }, [tqc]);
//     //
//     // const onTruckActorOrdering = useCallback((dxts: RefStableArray<Touchable<DispatchTruck>>) => {
//     //     tqc.setQueryData(dispatchTrucksActiveOrderTqk, dxts);
//     // }, [tqc]);
//     //
//
//     // const onBundleTouched = useCallback((nv: Touchable<DispatchBundle>) => {
//     //     tqc.setQueryData(bundleLastUpdatedTqk({bundleId: nv.value.bundle.id}), nv);
//     // }, [tqc]);
//     //
//     // const onStableBundleOrdering = useCallback((dxts: RefStableArray<Touchable<DispatchBundle>>) => {
//     //     tqc.setQueryData(dispatchBundlesStableOrderTqk, dxts);
//     // }, [tqc]);
//     //
//     // const onBundleActorOrdering = useCallback((dxbs: RefStableArray<Touchable<DispatchBundle>>) => {
//     //     tqc.setQueryData(dispatchBundlesActiveOrderTqk, dxbs);
//     // }, [tqc]);
//     //
//
//     // const bundles = useTouchableList({
//     //     extractKey: (dxb: DispatchBundle) => dxb.bundle.id,
//     //     sortComparators: dispatchBundleSorters,
//     // });
//
//     // this is essential in order for delta compression to work reliably.
//     // we have to maintain a query listener on every tripleg we see so
//     // that delta-compressed ones can be properly hydrated. otherwise, we
//     // have no way of knowing what stable relationships exist.
//     // const watchedTriplegIds = useSet<string>([]);
//     // const tqos = [...watchedTriplegIds.values().map(id =>
//     //     watchTriplegByIdTqo(mama, {triplegId: id}))];
//     // void useQueries({queries: tqos});
//
//     return <DynaMapProvider Context={TruckDynaMapContext} extractKey={extractDispatchTruckKey}>
//         <DynaMapProvider Context={BundleDynaMapContext} extractKey={extractDispatchBundleKey}>
//             <DynaMapProvider Context={WatchedTriplegIdDynaMapContext} extractKey={extractTriplegIdKey}>
//                 <DispatchMeshWatcher>
//                     {children}
//                 </DispatchMeshWatcher>
//             </DynaMapProvider>
//         </DynaMapProvider>
//     </DynaMapProvider>
// }
//

export function DispatchMeshWatcher({children}: PropsWithChildren) {
    const trucksTqrv = useDispatchTruckActorStubs();
    const bundlesTqrv = useDispatchBundleActorStubs();

    return <>
        {(trucksTqrv.data ?? []).map(stub =>
            <TruckTimelineRelay key={stub.id} truckId={stub.id}/>)}
        {(bundlesTqrv.data ?? []).map(stub => <>
                <BundleTimelineRelay key={`${stub.id}-timeline`} bundleId={stub.id}/>
                <BundleStatusRelay key={`${stub.id}-status`} bundleId={stub.id}/>
            </>
        )}

        {children}
    </>
}

// export const DispatchSearchParams = z.object({

//     facet: DispatchFacetEntity.default("bundle"),
// });
// export type DispatchSearchParams = z.infer<typeof DispatchSearchParams>;
//
